/*eslint no-sequences: 0*/
import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames/bind';

import Dot from './Dot';
import PageIndex from './PageIndex';
import styles from './PageLinks.less';

const cx = classnames.bind(styles);

const cap = (x) => x.replace(/^./, (c) => c.toUpperCase());
const generatePages = (c, fn) => Array.from({ length: c }).map((x, i) => fn(i + 1));

const DOTS = 'dots';
const LABELED = 'labeled';

/**
 * **Access:**
 * ```js
 * import { Pagination } from '@iss/client-react-components'
 *
 * ...
 * render () {
 * 	return <Pagination.PageLinks .../>
 * }
 * ```
 * A common page index control. Supports direct access to pages.
 */
export default class PageLinks extends React.PureComponent {
  static Style = {
    DOTS,
    LABELED,
  };

  static propTypes = {
    /** Selects how selectable pages are rendered, as dots or as a sliding enumerated page number range */
    type: PropTypes.oneOf([DOTS, LABELED]),
    /** The total number of pages */
    pageCount: PropTypes.number.isRequired,
    /** The current page (not index, 1 based)*/
    currentPage: PropTypes.number.isRequired,
    /** Custom classnames for various parts */
    classes: PropTypes.shape({
      container: PropTypes.string,
      dot: PropTypes.string,
    }),
    /** A function to produce a route or link target (href) for any given page. */
    pageLinkFactory: PropTypes.func,
    /** A callback to handle page transitions (if not routing) */
    onPageChange: PropTypes.func,
  };

  static defaultProps = {
    type: DOTS,
    pageLinkFactory: () => '#',
  };

  onPageChange(pageId, event) {
    const { onPageChange } = this.props;
    if (onPageChange) {
      event.preventDefault();
      onPageChange(pageId, event);
    }
  }

  render() {
    const { type } = this.props;
    const renderer = this[`render${cap(type)}`];

    return !renderer ? null : renderer.call(this);
  }

  renderDots() {
    const { classes = {}, className, pageCount, currentPage, pageLinkFactory } = this.props;

    return (
      <div className={cx('root', 'dots', className, classes.container)}>
        {generatePages(pageCount, (i) => (
          <Dot
            classes={classes}
            className={classes.dot}
            key={i}
            active={i === currentPage}
            href={pageLinkFactory(i, currentPage)}
            onClick={i === currentPage ? null : (e) => this.onPageChange(i, e)}
          />
        ))}
      </div>
    );
  }

  renderLabeled() {
    const { classes = {}, className, pageCount, currentPage, pageLinkFactory } = this.props;

    const start = Math.max(0, Math.min(currentPage + 2, pageCount) - 5);

    const outOfRange = (x) => x < 1 || x > pageCount;

    const Link = ({ disabled, page: i, label = i }) => (
      <PageIndex
        classes={classes}
        className={classes.label}
        active={currentPage === i && i === label}
        disabled={disabled || outOfRange(i)}
        label={label}
        href={outOfRange(i) ? null : pageLinkFactory(i, currentPage)}
        onClick={outOfRange(i) || i === currentPage ? null : (e) => this.onPageChange(i, e)}
      />
    );

    return (
      <div className={cx('root', 'labeled', className, classes.container)}>
        <Link label="<<" page={1} disabled={currentPage === 1} />
        <Link label="<" page={currentPage - 1} />
        {generatePages(5, (i) => ((i += start), (<Link key={i} page={i} />)))}
        <Link label=">" page={currentPage + 1} />
        <Link label=">>" page={pageCount} disabled={currentPage === pageCount} />
      </div>
    );
  }
}
